import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import resolveMediaUrl from '@/utils/resolveMediaUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Upload,
  ArrowLeft,
  Building2,
  MapPin,
  DollarSign,
  Ruler,
  Settings,
  Star,
  Search as SearchIcon,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ['Office', 'Retail', 'Mixed Use', 'SCO', 'IT Park', 'Co-working'];
const PROJECT_STATUSES = ['New Launch', 'Under Construction', 'Ready to Move'];
const LEASE_SALE_OPTIONS = ['For Sale', 'For Lease', 'Both'];
const BUILDING_GRADES = ['None', 'Grade A', 'Grade B', 'Grade A+'];
const HVAC_OPTIONS = ['None', 'Central AC', 'VRF', 'Split'];
const POWER_BACKUP_OPTIONS = ['None', '100%', 'Partial'];
const STRUCTURE_TYPES = ['None', 'RCC Frame', 'Steel Frame', 'Pre-fab'];
const PAYMENT_PLANS = ['None', 'Construction Linked', 'Down Payment', 'Flexi Plan'];
const GREEN_CERTS = ['None', 'LEED Gold', 'LEED Platinum', 'IGBC'];

const UNIT_TYPE_OPTIONS = ['Office', 'Shop', 'Showroom', 'SCO Plot', 'Food Court', 'Anchor Store'];
const HIGHWAY_OPTIONS = ['NH-48', 'Dwarka Expressway', 'SPR', 'Golf Course Rd', 'KMP', 'MG Road'];
const AMENITY_OPTIONS = ['Food Court', 'ATM', 'Gym', 'Cafeteria', 'Lounge', 'Rooftop', 'Swimming Pool', 'Conference Room', 'Business Centre', 'Retail Zone'];
const PARKING_TYPE_OPTIONS = ['Basement', 'Multi-level', 'Open'];
const FIRE_SAFETY_OPTIONS = ['Sprinklers', 'Fire NOC', 'Alarm', 'Hydrant'];
const BANK_OPTIONS = ['SBI', 'HDFC', 'ICICI', 'Axis', 'PNB', 'Kotak', 'Yes Bank'];

const SECTORS = Array.from({ length: 115 }, (_, i) => `Sector ${i + 1}`);

const emptyProject = {
  project_name: '', developer_name: '', project_type: 'Office', project_status: 'New Launch',
  rera_number: '', launch_date: '', possession_date: '', thumbnail: '', gallery_images: [],
  brochure_url: '', video_url: '', sector: '', micro_location: '', full_address: '',
  google_maps_link: '', latitude: '', longitude: '', nearby_highways: [],
  distance_cyber_city: '', distance_airport: '', nearest_metro: '',
  unit_types: [], size_min: '', size_max: '', floor_plate_size: '', total_floors: '',
  total_towers: '', total_units: '', efficiency_ratio: '',
  price_per_sqft: '', starting_price: '', lease_or_sale: 'For Sale', lease_price_sqft: '',
  maintenance_charge: '', cam_charges: '', assured_return_pct: '', assured_return_duration: '',
  payment_plan: 'None', bank_approvals: [],
  structure_type: 'None', ceiling_height: '', building_grade: 'None', glazing_type: '',
  hvac_system: 'None', lift_info: '', power_supply_kva: '', power_backup: 'None',
  internet_fibre: '', parking_ratio: '', parking_type: [],
  common_amenities: [], green_certification: 'None', rainwater_harvesting: false,
  solar_panels: false, ev_charging: false, cctv_security: false, fire_safety: [],
  wheelchair_accessible: false,
  nearby_corporates: '', nearby_malls: '', nearby_hospitals: '', nearby_hotels: '',
  nearest_metro_station: '', nearest_bus_stop: '',
  short_description: '', long_description: '', seo_slug: '', focus_keywords: [],
  is_featured: false, is_hot_deal: false, enquiry_cta_label: 'Get Best Price',
};

const MultiSelect = ({ options, selected, onChange, label }) => {
  return (
    <div>
      {label && <label className="text-sm font-medium mb-2 block">{label}</label>}
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => {
              if (selected.includes(opt)) {
                onChange(selected.filter(s => s !== opt));
              } else {
                onChange([...selected, opt]);
              }
            }}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              selected.includes(opt)
                ? 'bg-[#059669] text-white border-[#059669]'
                : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#059669]'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#F8FAFC] transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[#059669]" />}
          <h3 className="text-base font-bold text-[#0F172A]">{title}</h3>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t border-[#E2E8F0] pt-4">{children}</div>}
    </div>
  );
};

// ========== PROJECT LIST ==========
const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(res.data);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project deleted');
      setDeleteDialog(null);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  if (showForm || editProject) {
    return (
      <ProjectForm
        project={editProject}
        onBack={() => { setShowForm(false); setEditProject(null); }}
        onSaved={() => { setShowForm(false); setEditProject(null); fetchProjects(); }}
      />
    );
  }

  return (
    <div data-testid="projects-management">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Builder Projects</h1>
          <p className="text-sm text-[#64748B] mt-1">{projects.length} projects</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#059669] hover:bg-[#047857] text-white" data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border">
          <Building2 className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
          <p className="text-[#64748B]">No projects yet. Add your first builder project.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead>Project</TableHead>
                <TableHead>Developer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map(p => (
                <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {p.thumbnail ? (
                        <img src={resolveMediaUrl(p.thumbnail)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#94A3B8]" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-sm">{p.project_name}</p>
                        {p.is_hot_deal && <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full">HOT</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{p.developer_name}</TableCell>
                  <TableCell><span className="text-xs bg-[#ECFDF5] text-[#059669] px-2 py-1 rounded-full">{p.project_type}</span></TableCell>
                  <TableCell><span className="text-xs">{p.project_status}</span></TableCell>
                  <TableCell className="text-sm">{p.sector}</TableCell>
                  <TableCell>{p.is_featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : <Star className="w-4 h-4 text-[#D1D5DB]" />}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditProject(p)} data-testid={`edit-project-${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(p.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-project-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Project?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B]">This action cannot be undone.</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDelete(deleteDialog)}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== PROJECT FORM ==========
const ProjectForm = ({ project, onBack, onSaved }) => {
  const [form, setForm] = useState(project ? { ...emptyProject, ...project } : { ...emptyProject });
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const isEdit = !!project;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    const token = localStorage.getItem('admin_token');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      set('thumbnail', res.data.url);
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingThumb(false);
      e.target.value = '';
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingGallery(true);
    const token = localStorage.getItem('admin_token');
    const fd = new FormData();
    for (let f of files) fd.append('files', f);
    try {
      const res = await axios.post(`${API}/upload/images`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const urls = res.data.uploaded.map(img => img.url);
      set('gallery_images', [...form.gallery_images, ...urls]);
      toast.success(`${urls.length} images uploaded`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingGallery(false);
      e.target.value = '';
    }
  };

  const removeGalleryImage = (idx) => {
    set('gallery_images', form.gallery_images.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!form.project_name || !form.developer_name) {
      toast.error('Project name and developer are required');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    const payload = {
      ...form,
      size_min: form.size_min ? Number(form.size_min) : null,
      size_max: form.size_max ? Number(form.size_max) : null,
      floor_plate_size: form.floor_plate_size ? Number(form.floor_plate_size) : null,
      total_floors: form.total_floors ? Number(form.total_floors) : null,
      total_towers: form.total_towers ? Number(form.total_towers) : null,
      total_units: form.total_units ? Number(form.total_units) : null,
      price_per_sqft: form.price_per_sqft ? Number(form.price_per_sqft) : null,
      lease_price_sqft: form.lease_price_sqft ? Number(form.lease_price_sqft) : null,
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/projects/${project.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/projects`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Project created');
      }
      onSaved();
    } catch (err) {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="project-form">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">{isEdit ? 'Edit Project' : 'Add New Project'}</h1>
        </div>
        <Button onClick={handleSave} className="bg-[#059669] hover:bg-[#047857] text-white" disabled={saving} data-testid="save-project-btn">
          {saving ? 'Saving...' : 'Save Project'}
        </Button>
      </div>

      <div className="space-y-4">
        {/* 1. Basic Info */}
        <CollapsibleSection title="Basic Project Info" icon={Building2} defaultOpen={true}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Project Name *</label>
              <Input value={form.project_name} onChange={e => set('project_name', e.target.value)} placeholder="e.g. M3M Route 65" data-testid="project-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Developer / Builder *</label>
              <Input value={form.developer_name} onChange={e => set('developer_name', e.target.value)} placeholder="e.g. M3M India" data-testid="developer-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Project Type *</label>
              <Select value={form.project_type} onValueChange={v => set('project_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Project Status *</label>
              <Select value={form.project_status} onValueChange={v => set('project_status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">RERA Number</label>
              <Input value={form.rera_number} onChange={e => set('rera_number', e.target.value)} placeholder="RC/REP/HARERA/GGM/..." />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Launch Date</label>
              <Input type="date" value={form.launch_date} onChange={e => set('launch_date', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Possession Date</label>
              <Input type="month" value={form.possession_date} onChange={e => set('possession_date', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">YouTube / Video URL</label>
              <Input value={form.video_url || ''} onChange={e => set('video_url', e.target.value)} placeholder="https://youtube.com/..." />
            </div>
          </div>
          {/* Thumbnail */}
          <div>
            <label className="text-sm font-medium mb-2 block">Project Thumbnail *</label>
            <div className="flex items-center gap-4">
              {form.thumbnail && <img src={resolveMediaUrl(form.thumbnail)} alt="" className="w-24 h-24 rounded-lg object-cover" />}
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border border-dashed border-[#E2E8F0] rounded-lg hover:border-[#059669] transition-colors">
                <input type="file" accept="image/*" onChange={handleThumbUpload} className="hidden" data-testid="thumb-upload" />
                {uploadingThumb ? <div className="animate-spin w-5 h-5 border-2 border-[#059669] border-t-transparent rounded-full" /> : <Upload className="w-4 h-4 text-[#059669]" />}
                <span className="text-sm text-[#64748B]">{uploadingThumb ? 'Uploading...' : 'Upload Thumbnail'}</span>
              </label>
            </div>
          </div>
          {/* Gallery */}
          <div>
            <label className="text-sm font-medium mb-2 block">Gallery Images</label>
            <div className="flex flex-wrap gap-3 mb-3">
              {form.gallery_images.map((img, i) => (
                <div key={img} className="relative group">
                  <img src={resolveMediaUrl(img)} alt="" className="w-20 h-20 rounded-lg object-cover" />
                  <button type="button" onClick={() => removeGalleryImage(i)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border border-dashed border-[#E2E8F0] rounded-lg hover:border-[#059669] transition-colors w-fit">
              <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" data-testid="gallery-upload" />
              {uploadingGallery ? <div className="animate-spin w-5 h-5 border-2 border-[#059669] border-t-transparent rounded-full" /> : <Upload className="w-4 h-4 text-[#059669]" />}
              <span className="text-sm text-[#64748B]">{uploadingGallery ? 'Uploading...' : 'Add Gallery Images'}</span>
            </label>
          </div>
        </CollapsibleSection>

        {/* 2. Location */}
        <CollapsibleSection title="Location Details" icon={MapPin}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Sector *</label>
              <Select value={form.sector} onValueChange={v => set('sector', v)}>
                <SelectTrigger><SelectValue placeholder="Select Sector" /></SelectTrigger>
                <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Micro-location / Area</label>
              <Input value={form.micro_location} onChange={e => set('micro_location', e.target.value)} placeholder="e.g. Golf Course Ext Road" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Full Address</label>
              <Textarea value={form.full_address} onChange={e => set('full_address', e.target.value)} rows={2} placeholder="Complete address..." />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Google Maps Link</label>
              <Input value={form.google_maps_link || ''} onChange={e => set('google_maps_link', e.target.value)} placeholder="https://maps.google.com/..." />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nearest Metro Station</label>
              <Input value={form.nearest_metro || ''} onChange={e => set('nearest_metro', e.target.value)} placeholder="Station name + km" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Distance from Cyber City (km)</label>
              <Input value={form.distance_cyber_city || ''} onChange={e => set('distance_cyber_city', e.target.value)} placeholder="e.g. 5" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Distance from IGI Airport (km)</label>
              <Input value={form.distance_airport || ''} onChange={e => set('distance_airport', e.target.value)} placeholder="e.g. 25" />
            </div>
          </div>
          <MultiSelect options={HIGHWAY_OPTIONS} selected={form.nearby_highways} onChange={v => set('nearby_highways', v)} label="NH / Expressway Proximity" />
        </CollapsibleSection>

        {/* 3. Unit & Size */}
        <CollapsibleSection title="Unit & Size Details" icon={Ruler}>
          <MultiSelect options={UNIT_TYPE_OPTIONS} selected={form.unit_types} onChange={v => set('unit_types', v)} label="Unit Types Available *" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Size Min (sq ft)</label>
              <Input type="number" value={form.size_min || ''} onChange={e => set('size_min', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Size Max (sq ft)</label>
              <Input type="number" value={form.size_max || ''} onChange={e => set('size_max', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Floor Plate (sq ft)</label>
              <Input type="number" value={form.floor_plate_size || ''} onChange={e => set('floor_plate_size', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Total Floors</label>
              <Input type="number" value={form.total_floors || ''} onChange={e => set('total_floors', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Total Towers</label>
              <Input type="number" value={form.total_towers || ''} onChange={e => set('total_towers', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Total Units</label>
              <Input type="number" value={form.total_units || ''} onChange={e => set('total_units', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Efficiency Ratio (%)</label>
              <Input value={form.efficiency_ratio || ''} onChange={e => set('efficiency_ratio', e.target.value)} placeholder="e.g. 75%" />
            </div>
          </div>
        </CollapsibleSection>

        {/* 4. Pricing */}
        <CollapsibleSection title="Pricing & Payment" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Price per sq ft (BSP)</label>
              <Input type="number" value={form.price_per_sqft || ''} onChange={e => set('price_per_sqft', e.target.value)} placeholder="e.g. 18500" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Starting Price</label>
              <Input value={form.starting_price || ''} onChange={e => set('starting_price', e.target.value)} placeholder="e.g. 92.5 Lakhs" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Lease / Sale *</label>
              <Select value={form.lease_or_sale} onValueChange={v => set('lease_or_sale', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{LEASE_SALE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Lease Price/sq ft/month</label>
              <Input type="number" value={form.lease_price_sqft || ''} onChange={e => set('lease_price_sqft', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Maintenance Charge</label>
              <Input value={form.maintenance_charge || ''} onChange={e => set('maintenance_charge', e.target.value)} placeholder="per sq ft/month" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">CAM Charges</label>
              <Input value={form.cam_charges || ''} onChange={e => set('cam_charges', e.target.value)} placeholder="per sq ft/month" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Assured Return %</label>
              <Input value={form.assured_return_pct || ''} onChange={e => set('assured_return_pct', e.target.value)} placeholder="e.g. 12%" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Assured Return Duration</label>
              <Input value={form.assured_return_duration || ''} onChange={e => set('assured_return_duration', e.target.value)} placeholder="e.g. 36 months" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Payment Plan</label>
              <Select value={form.payment_plan} onValueChange={v => set('payment_plan', v)}>
                <SelectTrigger><SelectValue placeholder="Select Plan" /></SelectTrigger>
                <SelectContent>{PAYMENT_PLANS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <MultiSelect options={BANK_OPTIONS} selected={form.bank_approvals} onChange={v => set('bank_approvals', v)} label="Bank Approvals" />
        </CollapsibleSection>

        {/* 5. Specifications */}
        <CollapsibleSection title="Specifications" icon={Settings}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Structure Type</label>
              <Select value={form.structure_type} onValueChange={v => set('structure_type', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{STRUCTURE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Ceiling Height (ft)</label>
              <Input value={form.ceiling_height || ''} onChange={e => set('ceiling_height', e.target.value)} placeholder="e.g. 14" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Building Grade</label>
              <Select value={form.building_grade} onValueChange={v => set('building_grade', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{BUILDING_GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Glazing Type</label>
              <Input value={form.glazing_type || ''} onChange={e => set('glazing_type', e.target.value)} placeholder="e.g. Double glazed UPVC" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">HVAC System</label>
              <Select value={form.hvac_system} onValueChange={v => set('hvac_system', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{HVAC_OPTIONS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Lift Info</label>
              <Input value={form.lift_info || ''} onChange={e => set('lift_info', e.target.value)} placeholder="e.g. 4 Otis lifts" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Power Supply (KVA)</label>
              <Input value={form.power_supply_kva || ''} onChange={e => set('power_supply_kva', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Power Backup</label>
              <Select value={form.power_backup} onValueChange={v => set('power_backup', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{POWER_BACKUP_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Internet / Fibre</label>
              <Input value={form.internet_fibre || ''} onChange={e => set('internet_fibre', e.target.value)} placeholder="Providers available" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Parking Ratio</label>
              <Input value={form.parking_ratio || ''} onChange={e => set('parking_ratio', e.target.value)} placeholder="e.g. 1 per 400 sq ft" />
            </div>
          </div>
          <MultiSelect options={PARKING_TYPE_OPTIONS} selected={form.parking_type} onChange={v => set('parking_type', v)} label="Parking Type" />
        </CollapsibleSection>

        {/* 6. Amenities */}
        <CollapsibleSection title="Amenities & Features" icon={Star}>
          <MultiSelect options={AMENITY_OPTIONS} selected={form.common_amenities} onChange={v => set('common_amenities', v)} label="Common Amenities" />
          <MultiSelect options={FIRE_SAFETY_OPTIONS} selected={form.fire_safety} onChange={v => set('fire_safety', v)} label="Fire Safety" />
          <div>
            <label className="text-sm font-medium mb-1 block">Green Certification</label>
            <Select value={form.green_certification} onValueChange={v => set('green_certification', v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{GREEN_CERTS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              ['rainwater_harvesting', 'Rainwater Harvesting'],
              ['solar_panels', 'Solar Panels'],
              ['ev_charging', 'EV Charging'],
              ['cctv_security', 'CCTV / Security'],
              ['wheelchair_accessible', 'Wheelchair Accessible'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between bg-[#F8FAFC] px-4 py-3 rounded-lg">
                <span className="text-sm">{label}</span>
                <Switch checked={form[key]} onCheckedChange={v => set(key, v)} />
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* 7. Nearby Landmarks */}
        <CollapsibleSection title="Nearby Landmarks" icon={MapPin}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Nearby Offices / Corporates</label>
              <Input value={form.nearby_corporates || ''} onChange={e => set('nearby_corporates', e.target.value)} placeholder="e.g. Near Google, DLF Cybercity" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nearby Malls</label>
              <Input value={form.nearby_malls || ''} onChange={e => set('nearby_malls', e.target.value)} placeholder="e.g. Ambience Mall" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nearby Hospitals</label>
              <Input value={form.nearby_hospitals || ''} onChange={e => set('nearby_hospitals', e.target.value)} placeholder="e.g. Medanta, Fortis" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nearby Hotels (5-star)</label>
              <Input value={form.nearby_hotels || ''} onChange={e => set('nearby_hotels', e.target.value)} placeholder="e.g. Oberoi, Leela" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nearest Metro Station + km</label>
              <Input value={form.nearest_metro_station || ''} onChange={e => set('nearest_metro_station', e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Nearest Bus Stop + km</label>
              <Input value={form.nearest_bus_stop || ''} onChange={e => set('nearest_bus_stop', e.target.value)} />
            </div>
          </div>
        </CollapsibleSection>

        {/* 8. SEO & Marketing */}
        <CollapsibleSection title="SEO & Marketing" icon={SearchIcon}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Short Description (160 chars)</label>
              <Input value={form.short_description || ''} onChange={e => set('short_description', e.target.value)} maxLength={160} placeholder="Meta description for search engines" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Long Description</label>
              <Textarea value={form.long_description || ''} onChange={e => set('long_description', e.target.value)} rows={5} placeholder="Detailed project description..." />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">SEO Slug / URL</label>
              <Input value={form.seo_slug || ''} onChange={e => set('seo_slug', e.target.value)} placeholder="auto-generated-from-name" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Enquiry CTA Label</label>
              <Input value={form.enquiry_cta_label} onChange={e => set('enquiry_cta_label', e.target.value)} placeholder="e.g. Get Best Price" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between bg-[#F8FAFC] px-4 py-3 rounded-lg">
              <span className="text-sm font-medium">Featured Project</span>
              <Switch checked={form.is_featured} onCheckedChange={v => set('is_featured', v)} />
            </div>
            <div className="flex items-center justify-between bg-[#F8FAFC] px-4 py-3 rounded-lg">
              <span className="text-sm font-medium">Hot Deal Tag</span>
              <Switch checked={form.is_hot_deal} onCheckedChange={v => set('is_hot_deal', v)} />
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} className="bg-[#059669] hover:bg-[#047857] text-white px-8" disabled={saving} data-testid="save-project-btn-bottom">
          {saving ? 'Saving...' : 'Save Project'}
        </Button>
      </div>
    </div>
  );
};

export default ProjectsList;
